import { useState } from 'react';
import type { CreateEndpointRequest, UpdateEndpointRequest, Endpoint } from '@mockd/shared';
import { TIER_LIMITS } from '@mockd/shared';
import { stripTemplatesForValidation, COMMON_CONTENT_TYPES, validateEndpointInput } from '@mockd/shared/utils';
import { Alert, BinaryContentTypeHint, JsonEditor, TemplateVariableRef } from '../common';

interface EndpointFormCreateProps {
  endpoint?: undefined;
  onSubmit: (data: CreateEndpointRequest) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

interface EndpointFormEditProps {
  endpoint: Endpoint;
  onSubmit: (data: UpdateEndpointRequest) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

type EndpointFormProps = EndpointFormCreateProps | EndpointFormEditProps;

export function EndpointForm({ endpoint, onSubmit, onCancel, isLoading }: EndpointFormProps) {
  const isEdit = !!endpoint;

  const [path, setPath] = useState(endpoint?.path || '/');
  const [contentType, setContentType] = useState(endpoint?.contentType || 'application/json');
  const [customContentType, setCustomContentType] = useState(
    endpoint?.contentType && !COMMON_CONTENT_TYPES.includes(endpoint.contentType as typeof COMMON_CONTENT_TYPES[number])
      ? endpoint.contentType
      : ''
  );
  const isCustomContentType = !COMMON_CONTENT_TYPES.includes(contentType as typeof COMMON_CONTENT_TYPES[number]);
  const [responseBody, setResponseBody] = useState(
    endpoint?.responseBody || '{\n  "message": "Hello, World!"\n}'
  );
  const [statusCode, setStatusCode] = useState(endpoint?.statusCode?.toString() || '200');
  const [delay, setDelay] = useState(endpoint?.delay?.toString() || '0');
  const [delayMax, setDelayMax] = useState(endpoint?.delayMax?.toString() || '0');
  const [rateLimit, setRateLimit] = useState(endpoint?.rateLimit?.toString() || String(TIER_LIMITS.free.defaultEndpointRateLimit));
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const statusCodeNum = parseInt(statusCode, 10);
    const delayNum = parseInt(delay, 10);
    const delayMaxNum = parseInt(delayMax, 10);
    const rateLimitNum = parseInt(rateLimit, 10);

    const validation = validateEndpointInput(
      {
        path: path.trim(),
        statusCode: isNaN(statusCodeNum) ? undefined : statusCodeNum,
        delay: isNaN(delayNum) ? undefined : delayNum,
        delayMax: isNaN(delayMaxNum) ? undefined : delayMaxNum,
        rateLimit: isNaN(rateLimitNum) ? undefined : rateLimitNum,
      },
      'free',
      { requirePath: !isEdit }
    );

    if (isNaN(statusCodeNum)) {
      validation.valid = false;
      validation.errors.push({ error: 'Status code must be a number', code: 'STATUS_CODE_INVALID', field: 'statusCode' });
    }
    if (isNaN(delayNum)) {
      validation.valid = false;
      validation.errors.push({ error: 'Delay must be a number', code: 'DELAY_INVALID', field: 'delay' });
    }
    if (isNaN(delayMaxNum)) {
      validation.valid = false;
      validation.errors.push({ error: 'Delay max must be a number', code: 'DELAY_MAX_INVALID', field: 'delayMax' });
    }
    if (isNaN(rateLimitNum)) {
      validation.valid = false;
      validation.errors.push({ error: 'Rate limit must be a number', code: 'RATE_LIMIT_INVALID', field: 'rateLimit' });
    }

    if (!validation.valid) {
      setError(validation.errors[0].error);
      return;
    }

    const effectiveContentType = isCustomContentType ? customContentType : contentType;

    if (!effectiveContentType.trim()) {
      setError('Content type is required');
      return;
    }

    // Only validate JSON structure when content type is application/json
    if (effectiveContentType === 'application/json') {
      try {
        JSON.parse(stripTemplatesForValidation(responseBody));
      } catch {
        setError('Response body must be valid JSON (template variables like {{$uuid}} are allowed inside values)');
        return;
      }
    }

    try {
      if (isEdit) {
        const data: UpdateEndpointRequest = { contentType: effectiveContentType, responseBody, statusCode: statusCodeNum, delay: delayNum, delayMax: delayMaxNum, rateLimit: rateLimitNum };
        await (onSubmit as (data: UpdateEndpointRequest) => Promise<void>)(data);
      } else {
        const data: CreateEndpointRequest = { path: path.trim(), contentType: effectiveContentType, responseBody, statusCode: statusCodeNum, delay: delayNum, delayMax: delayMaxNum, rateLimit: rateLimitNum };
        await (onSubmit as (data: CreateEndpointRequest) => Promise<void>)(data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save endpoint');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <Alert variant="error">{error}</Alert>
      )}

      {!isEdit && (
        <div className="fieldset">
          <label htmlFor="path" className="fieldset-legend">Path</label>
          <input
            type="text"
            id="path"
            value={path}
            onChange={(e) => setPath(e.target.value)}
            placeholder="/api/users"
            className="input w-full font-mono"
            disabled={isLoading}
          />
          <p className="label whitespace-normal text-base-content/80">All HTTP methods (GET, POST, PUT, etc.) will be accepted at this path</p>
        </div>
      )}

      <div className="fieldset">
        <label htmlFor="contentType" className="fieldset-legend">Content Type</label>
        <div className="flex gap-2">
          <select
            id="contentType"
            value={isCustomContentType ? '__custom__' : contentType}
            onChange={(e) => {
              if (e.target.value === '__custom__') {
                setContentType(customContentType || '');
              } else {
                setContentType(e.target.value);
              }
            }}
            className="select flex-1"
            disabled={isLoading}
          >
            {COMMON_CONTENT_TYPES.map((ct) => (
              <option key={ct} value={ct}>{ct}</option>
            ))}
            <option value="__custom__">Custom...</option>
          </select>
          {isCustomContentType && (
            <input
              type="text"
              value={customContentType}
              onChange={(e) => {
                setCustomContentType(e.target.value);
                setContentType(e.target.value);
              }}
              placeholder="e.g. application/vnd.api+json"
              className="input flex-1 font-mono"
              disabled={isLoading}
            />
          )}
        </div>
        <BinaryContentTypeHint contentType={contentType} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="fieldset">
          <label htmlFor="statusCode" className="fieldset-legend">Status Code</label>
          <input
            type="number"
            id="statusCode"
            value={statusCode}
            onChange={(e) => setStatusCode(e.target.value)}
            min="100"
            max="599"
            className="input w-full"
            disabled={isLoading}
          />
        </div>

        <div className="fieldset">
          <label htmlFor="delay" className="fieldset-legend">Delay (ms)</label>
          <input
            type="number"
            id="delay"
            value={delay}
            onChange={(e) => setDelay(e.target.value)}
            min="0"
            className="input w-full"
            disabled={isLoading}
          />
        </div>

        <div className="fieldset">
          <label htmlFor="delayMax" className="fieldset-legend">Delay Max (ms)</label>
          <input
            type="number"
            id="delayMax"
            value={delayMax}
            onChange={(e) => setDelayMax(e.target.value)}
            min="0"
            className="input w-full"
            disabled={isLoading}
          />
          <p className="label whitespace-normal text-base-content/80">Randomize delay between min and max</p>
        </div>

        <div className="fieldset">
          <label htmlFor="rateLimit" className="fieldset-legend">Rate Limit (req/min)</label>
          <input
            type="number"
            id="rateLimit"
            value={rateLimit}
            onChange={(e) => setRateLimit(e.target.value)}
            min="1"
            className="input w-full"
            disabled={isLoading}
          />
        </div>
      </div>

      <div className="fieldset">
        <label htmlFor="responseBody" className="fieldset-legend">
          Response Body{contentType === 'application/json' ? ' (JSON)' : ''}
        </label>
        {contentType === 'application/json' ? (
          <JsonEditor
            id="responseBody"
            value={responseBody}
            onChange={setResponseBody}
            rows={8}
            disabled={isLoading}
            templateAware
          />
        ) : (
          <textarea
            id="responseBody"
            value={responseBody}
            onChange={(e) => setResponseBody(e.target.value)}
            rows={8}
            className="textarea w-full font-mono text-sm"
            disabled={isLoading}
          />
        )}
        <TemplateVariableRef />
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-ghost"
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : isEdit ? 'Update Endpoint' : 'Create Endpoint'}
        </button>
      </div>
    </form>
  );
}
